import { useState, useEffect } from 'react';
import { Card } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button';
import { Switch } from '../../../../components/ui/Switch';
import { FileText, AlertTriangle, Loader2 } from 'lucide-react';
import { settingsApi } from '../../../../services/api/endpoints/settings';
import { usePermissions } from '../../../../hooks/usePermissions';
import type { EInvoiceIntegrationConfig } from '../../../../types/settings';
import toast from 'react-hot-toast';

const DEFAULT: EInvoiceIntegrationConfig = {
  provider: 'dgi_congo',
  enabled: false,
  taxpayerNumber: '',
  autoSubmit: false,
  validateBeforeSubmit: true,
  syncInvoices: false,
};

export function EInvoiceIntegration() {
  const { isAdmin, can } = usePermissions();
  const canManage = can('manage_integrations') || isAdmin;

  const [config, setConfig] = useState<EInvoiceIntegrationConfig>(DEFAULT);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await settingsApi.getEInvoiceSettings();
        if (!res.success || !res.data) throw new Error();
        setConfig({ ...DEFAULT, ...res.data });
      } catch {
        setError('Impossible de charger la configuration de facturation électronique.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const save = async () => {
    if (!canManage) return;
    setIsSaving(true);
    setError(null);
    try {
      const res = await settingsApi.updateIntegrations({ eInvoicing: config });
      if (!res.success) throw new Error();
      toast.success('Configuration e-facture enregistrée');
    } catch {
      setError('Impossible de sauvegarder. Veuillez réessayer.');
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const set = <K extends keyof EInvoiceIntegrationConfig>(key: K, value: EInvoiceIntegrationConfig[K]) =>
    setConfig(prev => ({ ...prev, [key]: value }));

  if (isLoading) {
    return (
      <Card title="Facturation électronique (DGI)" icon={FileText}>
        <div className="flex items-center justify-center h-40 gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Chargement...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Facturation électronique (DGI)" icon={FileText}>
      <div className="space-y-5">
        {error && (
          <div className="flex items-start gap-3 p-3 rounded-lg border bg-destructive/5 border-destructive/20 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          Connectez votre système comptable au portail de facturation électronique de la DGI (Direction Générale des Impôts).
        </p>

        {/* Enable / Disable */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Activer la facturation électronique DGI</p>
            <p className="text-xs text-muted-foreground">Soumettre et recevoir des factures via le portail officiel</p>
          </div>
          <Switch
            checked={config.enabled}
            onCheckedChange={v => set('enabled', v)}
            disabled={!canManage || isSaving}
          />
        </div>

        {/* Taxpayer number */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-foreground">Numéro d’Identification Fiscale (NIF)</label>
          <input
            className="w-full text-sm border border-border rounded-md px-3 py-1.5 bg-background text-foreground placeholder:text-muted-foreground"
            placeholder="ex: CD1234567890"
            value={config.taxpayerNumber}
            onChange={e => set('taxpayerNumber', e.target.value)}
            disabled={!canManage || isSaving}
          />
        </div>

        {/* Toggles */}
        <div className="border-t border-border pt-4 space-y-1">
          {([
            { key: 'autoSubmit',            label: 'Soumission automatique',        desc: 'Envoyer automatiquement les factures à la DGI' },
            { key: 'validateBeforeSubmit',   label: 'Valider avant soumission',      desc: 'Afficher une confirmation avant chaque envoi' },
            { key: 'syncInvoices',           label: 'Synchroniser les factures',     desc: 'Importer les factures reçues depuis le portail DGI' },
          ] as const).map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between rounded-lg px-3 py-2.5 hover:bg-muted/50 transition-colors">
              <div>
                <p className="text-sm font-medium text-foreground">{label}</p>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
              <Switch
                checked={!!config[key]}
                onCheckedChange={v => set(key, v as any)}
                disabled={!canManage || isSaving}
                className="ml-4 flex-shrink-0"
              />
            </div>
          ))}
        </div>

        {canManage && (
          <div className="flex justify-end pt-2 border-t border-border">
            <Button onClick={save} disabled={isSaving}>
              {isSaving ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Enregistrement...</> : 'Enregistrer'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}